import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useShortcuts } from "@/context/ShortcutsContext";

// Combos we never bind because browsers / OS depend on them for their own
// well-known behaviours. `Ctrl+P` was intentionally allowed for Record
// Payment (we pre-empt the Print dialog with preventDefault).
const BLOCKED = new Set(["Ctrl+C", "Ctrl+V", "Ctrl+X", "Ctrl+A", "Ctrl+Z", "Ctrl+Y", "Ctrl+T", "Ctrl+W", "Ctrl+N", "Ctrl+S", "Ctrl+F"]);

function comboOf(e) {
    if (!e.ctrlKey && !e.altKey && !e.metaKey) return "";
    const parts = [];
    if (e.ctrlKey || e.metaKey) parts.push("Ctrl");
    if (e.altKey) parts.push("Alt");
    if (e.shiftKey) parts.push("Shift");
    const k = (e.key || "").toUpperCase();
    if (k.length === 1 && /[A-Z0-9]/.test(k)) parts.push(k);
    else return "";
    return parts.join("+");
}

/** Attach one global keydown listener that fires shortcut actions.
 *  Navigation actions push the router; interactive actions emit a
 *  `micm-shortcut` window event so pages can listen. */
export default function useGlobalHotkeys() {
    const { byAction, shortcuts } = useShortcuts();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const byCombo = Object.fromEntries((shortcuts || []).map((s) => [(s.combo || "").toUpperCase(), s]));

        const handler = (e) => {
            // v1.29.08 — Reliability rewrite for Ctrl+L (and every other
            // shortcut). Three fixes rolled together:
            //
            // 1. Was: bailed out entirely when focus was in an input /
            //    textarea / select / contenteditable. That killed Ctrl+L
            //    the instant a search box grabbed focus — the exact
            //    complaint. Now we resolve the combo FIRST, and only skip
            //    if the resolved combo happens to be one the browser uses
            //    for text editing (Ctrl+A / C / V / X / Z / Y — already
            //    in the BLOCKED set so this collapses to a no-op).
            //
            // 2. `preventDefault()` fires the moment we recognise a bound
            //    combo, BEFORE any of our own routing logic runs. That
            //    stops the browser's address-bar hijack on Ctrl+L
            //    (Chrome/Firefox), the Downloads panel on Ctrl+J, etc.
            //    Also `stopPropagation()` in case another handler on
            //    the same target might swallow it downstream.
            //
            // 3. The listener is registered in the CAPTURE phase, so we
            //    intercept before component-level handlers (e.g. shadcn
            //    Command's built-in Ctrl+K, a page's local onKeyDown).
            //    Together with the input-focus fix, this is what makes
            //    Ctrl+L work from every focus state in the app.
            const combo = comboOf(e).toUpperCase();
            if (!combo) return;
            const s = byCombo[combo];
            if (!s) return;
            if (BLOCKED.has(combo)) return;
            // If the user is typing in a text field AND the combo is one
            // of the browser's text-editing defaults, we already left via
            // the BLOCKED check above. Any other combo (Ctrl+L / Ctrl+D /
            // etc.) is safe to fire regardless of focus.
            e.preventDefault();
            e.stopPropagation();
            // "Already on target route" fast-path: just fire the event so
            // pages can re-focus their key input (e.g. Party picker on
            // /admin/reports). Skip the navigate — a same-URL navigate is
            // a no-op but still trips the location.pathname effect in
            // some layouts and causes flicker.
            if (s.target_route) {
                const target = s.target_route.split("?")[0];
                if (target && target !== location.pathname) {
                    navigate(s.target_route);
                }
            }
            window.dispatchEvent(new CustomEvent("micm-shortcut", { detail: { action_id: s.action_id, combo } }));
        };

        // Capture-phase listener — runs before any bubbling handler on the
        // event's target chain.
        window.addEventListener("keydown", handler, true);
        return () => window.removeEventListener("keydown", handler, true);
    }, [byAction, shortcuts, navigate, location.pathname]);
}
